import { Fragment, useMemo, useState } from 'react';
import { Stat, StatusChip } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatDate, num, pct, toLocalDateString, todayLocal, weekRange } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface Summary {
  entries: number;
  hours: number;
  timed: number;
  untimed: number;
  backwards: number;
  overlapPairs: number;
  overlapEntries: number;
  overlapHours: number;
  sameDay: number;
  sameDayPct: number | null;
  late: number;
  latePct: number | null;
  medianLagDays: number | null;
  worstLagDays: number | null;
}
interface Technician extends Summary {
  id: number;
  name: string;
  active: boolean;
  /** False for somebody on Autotask's roster with no time in these days. */
  worked: boolean;
}
interface Entry {
  id: number;
  worked: string | null;
  start: string | null;
  end: string | null;
  timed: boolean;
  backwards: boolean;
  hours: number;
  clockHours: number | null;
  created: string | null;
  lagDays: number | null;
  lagHours: number | null;
  bucket: string | null;
  overlapsWith: number[];
  posted: boolean;
  nonBillable: boolean;
  summary: string | null;
  ticketId: number | null;
  ticketNumber: string | null;
  title: string | null;
  kind: 'ticket' | 'task' | 'other';
  account: string | null;
  link: string | null;
  workType: string;
}
interface Overlap {
  day: string;
  from: string;
  to: string;
  minutes: number;
  hours: number;
  a: Entry;
  b: Entry;
}
interface DayRow {
  day: string;
  spanStart: string;
  spanEnd: string;
  hours: number;
  clockHours: number;
  entries: Entry[];
}
interface Bucket {
  key: string;
  label: string;
  entries: number;
  hours: number;
  pct: number;
}
interface Detail {
  id: number;
  name: string;
  active: boolean;
  totals: Summary;
  buckets: Bucket[];
  overlaps: Overlap[];
  days: DayRow[];
  entries: Entry[];
}
interface Payload {
  from: string;
  to: string;
  tz: string;
  lateAfterDays: number;
  roster: { count: number; readUtc: string; unavailable: string | null };
  totals: Summary;
  technicians: Technician[];
  detail: Detail | null;
  fetchedUtc: string;
}

/* The report is read in the browser's own zone, so a clock drawn here is the
   clock the technician saw. The API is told which zone that is. */
const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

const clockOf = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—');
const stampOf = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const h = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${num(n, 2)} h`);

/** How late an entry was, in words. */
function lagText(days: number | null) {
  if (days === null) return 'Not known';
  if (days < 0) return `${num(-days)} ${-days === 1 ? 'day' : 'days'} early`;
  if (days === 0) return 'Same day';
  if (days === 1) return 'Next day';
  return `${num(days)} days later`;
}

/** The ladder the page colours by: same day is clean, over a week is not. */
function lagBand(days: number | null) {
  if (days === null) return 'neutral' as const;
  if (days <= 0) return 'good' as const;
  if (days === 1) return 'neutral' as const;
  if (days <= 3) return 'warning' as const;
  if (days <= 7) return 'serious' as const;
  return 'critical' as const;
}

function quick(k: 'week' | 'lastWeek' | 'month' | 'lastMonth' | 'quarter'): { from: string; to: string } {
  const d = new Date();
  const ld = toLocalDateString;
  switch (k) {
    case 'week':
      return weekRange('current');
    case 'lastWeek':
      return weekRange('last');
    case 'month':
      return { from: ld(new Date(d.getFullYear(), d.getMonth(), 1)), to: todayLocal() };
    case 'lastMonth':
      return { from: ld(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: ld(new Date(d.getFullYear(), d.getMonth(), 0)) };
    default:
      return { from: ld(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)), to: todayLocal() };
  }
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'week', label: 'This week' },
  { k: 'lastWeek', label: 'Last week' },
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'quarter', label: 'This quarter' },
];

/**
 * Reports › Technician Cleanliness — one technician's time entries judged on
 * how they were KEPT rather than what they were for: clocks that overlap each
 * other, and how long after the day worked each entry was written down
 * (routes/reportsTechnicianCleanliness.ts).
 */
export function TechnicianCleanlinessReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('lastMonth'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [who, setWho] = useState<number | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  /* The entry table is long; this is the only filter it needs. */
  const [lateOnly, setLateOnly] = useState(false);

  const params = new URLSearchParams({ from, to, tz: TZ });
  if (who !== null) params.set('resourceId', String(who));
  const data = useHeldResource<Payload>(`/reports/technician-cleanliness?${params}`);
  const d = data.data;
  const detail = d?.detail ?? null;

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  const people = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (d?.technicians ?? []).filter((t) => (!activeOnly || t.active) && (!q || t.name.toLowerCase().includes(q)));
  }, [d, activeOnly, search]);

  /* The picker lists everyone, filters or no filters: choosing a name you
     cannot see in the table below would be the one thing it is for. */
  const choices = useMemo(() => [...(d?.technicians ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [d]);

  const shownEntries = useMemo(
    () => (detail?.entries ?? []).filter((e) => !lateOnly || (e.lagDays !== null && e.lagDays > (d?.lateAfterDays ?? 1))),
    [detail, lateOnly, d],
  );

  const exportCsv = () => {
    if (!detail) return;
    downloadCsv(`technician_cleanliness_${detail.name.replace(/[^A-Za-z0-9]+/g, '_')}_${from}_to_${to}_${fileStamp()}.csv`, [
      ['Technician', detail.name],
      ['Days worked', `${from} to ${to}`],
      ['Times shown in', TZ],
      [],
      ['Overlapping time'],
      ['Day', 'Overlap from', 'Overlap to', 'Minutes', 'Entry A', 'A ticket', 'A account', 'A start', 'A end', 'Entry B', 'B ticket', 'B account', 'B start', 'B end'],
      ...detail.overlaps.map((o) => [
        o.day,
        stampOf(o.from),
        stampOf(o.to),
        o.minutes,
        o.a.id,
        o.a.ticketNumber ?? '',
        o.a.account ?? '',
        stampOf(o.a.start),
        stampOf(o.a.end),
        o.b.id,
        o.b.ticketNumber ?? '',
        o.b.account ?? '',
        stampOf(o.b.start),
        stampOf(o.b.end),
      ]),
      [],
      ['Every time entry'],
      ['Entry', 'Day worked', 'Start', 'End', 'Hours claimed', 'Hours on the clock', 'Ticket', 'Account', 'Work type', 'Created', 'Days to enter', 'Hours after the work', 'Overlaps', 'Posted', 'Notes'],
      ...detail.entries.map((e) => [
        e.id,
        e.worked ?? '',
        stampOf(e.start),
        stampOf(e.end),
        e.hours,
        e.clockHours ?? '',
        e.ticketNumber ?? '',
        e.account ?? '',
        e.workType,
        stampOf(e.created),
        e.lagDays ?? '',
        e.lagHours ?? '',
        e.overlapsWith.length,
        e.posted ? 'Yes' : 'No',
        e.summary ?? '',
      ]),
    ]);
  };

  return (
    <>
      <PageHead
        title="Technician Cleanliness"
        lede="One technician's time entries judged on how they were kept: clocks that overlap each other, and how long after the day worked each entry was written down."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Technician">
              <select className="select" style={{ minWidth: 240 }} value={who ?? ''} onChange={(e) => setWho(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Choose a technician…</option>
                {choices.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {!t.active ? ' (inactive)' : ''}
                    {t.worked ? ` — ${num(t.entries)} entries` : ' — no time in these days'}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Day worked from">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((q) => (
                <button key={q.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(q.k))}>
                  {q.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-3" style={{ alignItems: 'center', gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!detail} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          {d && (
            <p className="xs muted mt-2">
              {formatDate(d.from)} – {formatDate(d.to)} · {num(d.totals.entries)} time entries across {num(d.technicians.filter((t) => t.worked).length)}{' '}
              technicians. Clocks are shown in {d.tz}, this browser's zone, so they read as the technician saw them.
              {d.roster.unavailable
                ? ` Autotask's roster could not be read (${d.roster.unavailable}), so the list is only people with time in these days.`
                : ` ${num(d.roster.count)} technicians on Autotask's roster.`}
            </p>
          )}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading time entries from Autotask" />}

      {detail && <DetailView detail={detail} lateAfterDays={d?.lateAfterDays ?? 1} entries={shownEntries} lateOnly={lateOnly} setLateOnly={setLateOnly} />}

      {d && (
        <section className="card mt-4">
          <div className="card-body row-between" style={{ alignItems: 'flex-end' }}>
            <div>
              <h3>Everyone in these days</h3>
              <p className="xs muted">Sorted by overlapping pairs. Choose a name to open that technician.</p>
            </div>
            <div className="row wrap" style={{ gap: 'var(--space-3)', alignItems: 'center' }}>
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name" />
              <label className="checkbox">
                <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                Active only
              </label>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Technician</th>
                  <th className="num">Entries</th>
                  <th className="num">Hours</th>
                  <th className="num">Overlapping pairs</th>
                  <th className="num">Hours overlapped</th>
                  <th className="num">No start/end</th>
                  <th className="num">Same day</th>
                  <th className="num">Median days to enter</th>
                </tr>
              </thead>
              <tbody>
                {people.length === 0 && (
                  <tr>
                    <td colSpan={8} className="muted small">
                      No technicians match.
                    </td>
                  </tr>
                )}
                {people.map((t) => (
                  <tr key={t.id} className={t.id === who ? 'is-open' : undefined}>
                    <td>
                      <button type="button" className="link-button" onClick={() => setWho(t.id)}>
                        {t.name}
                      </button>
                      {!t.active && <span className="xs muted"> · inactive</span>}
                      {!t.worked && <span className="xs muted"> · no time in these days</span>}
                    </td>
                    <td className="num">{num(t.entries)}</td>
                    <td className="num">{h(t.hours)}</td>
                    <td className="num">{t.overlapPairs ? <strong>{num(t.overlapPairs)}</strong> : '—'}</td>
                    <td className="num">{t.overlapHours ? h(t.overlapHours) : '—'}</td>
                    <td className="num">{t.untimed ? num(t.untimed) : '—'}</td>
                    <td className="num">{pct(t.sameDayPct)}</td>
                    <td className="num">{t.medianLagDays === null ? '—' : num(t.medianLagDays, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function DetailView({
  detail,
  lateAfterDays,
  entries,
  lateOnly,
  setLateOnly,
}: {
  detail: Detail;
  lateAfterDays: number;
  entries: Entry[];
  lateOnly: boolean;
  setLateOnly: (v: boolean) => void;
}) {
  const t = detail.totals;
  const [openDay, setOpenDay] = useState<string | null>(detail.days[0]?.day ?? null);

  if (t.entries === 0) {
    return (
      <section className="card mb-4">
        <div className="card-body">
          <h3>{detail.name}</h3>
          <p className="small muted">No time entries in these days.</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <div className="grid grid-4 mb-4">
        <Stat label="Time entries" value={num(t.entries)} foot={`${h(t.hours)} worked`} />
        <Stat
          label="Overlapping pairs"
          value={num(t.overlapPairs)}
          flagged={t.overlapPairs > 0}
          foot={t.overlapPairs ? `${h(t.overlapHours)} counted twice, over ${num(t.overlapEntries)} entries` : 'no clocks clash'}
        />
        <Stat
          label="Entered same day"
          value={pct(t.sameDayPct)}
          foot={`${num(t.late)} entered more than ${num(lateAfterDays)} ${lateAfterDays === 1 ? 'day' : 'days'} late`}
        />
        <Stat
          label="Median days to enter"
          value={t.medianLagDays === null ? '—' : num(t.medianLagDays, 1)}
          foot={t.worstLagDays === null ? '' : `worst ${lagText(t.worstLagDays).toLowerCase()}`}
        />
      </div>

      {/* How much of this person's time can be checked for an overlap at all.
          Entries with no clocks are not clean — they are unmeasured, and
          saying so is the difference between a pass and a blank. */}
      <section className="card mb-4">
        <div className="card-body small">
          <strong>{num(t.timed)}</strong> of {num(t.entries)} entries carry both a start and an end time, so only those could be checked for an
          overlap.{' '}
          {t.untimed > 0 && (
            <>
              <strong>{num(t.untimed)}</strong> {t.untimed === 1 ? 'entry has' : 'entries have'} no clocks at all and could not be checked.{' '}
            </>
          )}
          {t.backwards > 0 && (
            <>
              <strong>{num(t.backwards)}</strong> {t.backwards === 1 ? 'entry ends' : 'entries end'} at or before{' '}
              {t.backwards === 1 ? 'its' : 'their'} own start, which is a mistyped clock rather than a span.
            </>
          )}
        </div>
      </section>

      {/* --- overlapping time ------------------------------------------- */}
      <section className="card mb-4">
        <div className="card-body">
          <h3>Overlapping time</h3>
          <p className="xs muted">
            Two entries by {detail.name} whose clocks run over each other. One body cannot be in two places at once, so each pair is either a
            mistyped time or an hour counted twice.
          </p>
        </div>
        {detail.overlaps.length === 0 ? (
          <div className="card-body small muted" style={{ paddingTop: 0 }}>
            No overlaps in {num(t.timed)} entries with clocks.
          </div>
        ) : (
          <>
            {/* The days it happened on, drawn end to end: the clash is easier
                to see than to read off a table. */}
            <div className="card-body" style={{ paddingTop: 0 }}>
              <div className="row wrap" style={{ gap: 4 }}>
                {detail.days.map((day) => (
                  <button
                    key={day.day}
                    type="button"
                    className={`btn btn-sm ${openDay === day.day ? 'btn-secondary' : 'btn-ghost'}`}
                    onClick={() => setOpenDay(day.day === openDay ? null : day.day)}
                  >
                    {formatDate(day.day)}
                  </button>
                ))}
              </div>
              {detail.days
                .filter((day) => day.day === openDay)
                .map((day) => (
                  <DayTimeline key={day.day} day={day} />
                ))}
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th className="num">Overlap</th>
                    <th>Entry</th>
                    <th>Clock</th>
                    <th>Ticket</th>
                    <th>Account</th>
                    <th>Work type</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.overlaps.map((o) => (
                    <Fragment key={`${o.a.id}-${o.b.id}`}>
                      <tr className="tc-pair-top">
                        <td rowSpan={2}>{formatDate(o.day)}</td>
                        <td rowSpan={2} className="num">
                          <strong>{o.minutes < 60 ? `${num(o.minutes)} min` : h(o.hours)}</strong>
                          <div className="xs muted">
                            {clockOf(o.from)} – {clockOf(o.to)}
                          </div>
                        </td>
                        <td className="xs mono">#{o.a.id}</td>
                        <td className="xs mono">
                          {clockOf(o.a.start)} – {clockOf(o.a.end)}
                        </td>
                        <td className="xs">
                          <TicketCell entry={o.a} />
                        </td>
                        <td className="xs">{o.a.account ?? '—'}</td>
                        <td className="xs">{o.a.workType}</td>
                      </tr>
                      <tr className="tc-pair-bottom">
                        <td className="xs mono">#{o.b.id}</td>
                        <td className="xs mono">
                          {clockOf(o.b.start)} – {clockOf(o.b.end)}
                        </td>
                        <td className="xs">
                          <TicketCell entry={o.b} />
                        </td>
                        <td className="xs">{o.b.account ?? '—'}</td>
                        <td className="xs">{o.b.workType}</td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* --- timeliness --------------------------------------------------- */}
      <section className="card mb-4">
        <div className="card-body">
          <h3>Timeliness</h3>
          <p className="xs muted">
            How long after the day worked each entry was created. Time written up a week later is remembered time: the notes are thinner, the hours
            rounder, and the invoice waits on it.
          </p>
          <div className="tc-buckets mt-3">
            {detail.buckets.map((b) => (
              <div key={b.key} className="tc-bucket">
                <div className="row-between">
                  <span className="small">{b.label}</span>
                  <span className="mono xs">
                    {num(b.entries)} · {pct(b.pct)}
                  </span>
                </div>
                <span className="tc-bucket-track">
                  <span className={`tc-bucket-fill is-${b.key}`} style={{ width: `${Math.min(100, b.pct)}%` }} />
                </span>
                <span className="xs muted">{h(b.hours)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- every entry -------------------------------------------------- */}
      <section className="card">
        <div className="card-body row-between" style={{ alignItems: 'center' }}>
          <h3>Every time entry</h3>
          <label className="checkbox">
            <input type="checkbox" checked={lateOnly} onChange={(e) => setLateOnly(e.target.checked)} />
            Only entries more than {num(lateAfterDays)} {lateAfterDays === 1 ? 'day' : 'days'} late
          </label>
        </div>
        <div className="table-wrap">
          <table className="table tc-entries">
            <thead>
              <tr>
                <th>Day worked</th>
                <th>Clock</th>
                <th className="num">Hours</th>
                <th>Ticket</th>
                <th>Account</th>
                <th>Entered</th>
                <th>Delay</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted small">
                    {lateOnly ? 'Nothing was entered late in these days.' : 'No time entries in these days.'}
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="small">
                    {formatDate(e.worked)}
                    {e.overlapsWith.length > 0 && (
                      <>
                        {' '}
                        <span className="badge badge-critical" title={`Overlaps entry ${e.overlapsWith.map((x) => `#${x}`).join(', ')}`}>
                          overlaps
                        </span>
                      </>
                    )}
                  </td>
                  <td className="xs mono">
                    {e.timed || e.backwards ? (
                      <>
                        {clockOf(e.start)} – {clockOf(e.end)}
                        {e.backwards && <span className="xs muted"> · ends before it starts</span>}
                      </>
                    ) : (
                      <span className="muted">no start or end</span>
                    )}
                  </td>
                  <td className="num small">
                    {h(e.hours)}
                    {/* The clocks and the hours claimed are two separate
                        fields in Autotask, and they disagree often enough to
                        be worth showing where they do. */}
                    {e.clockHours !== null && Math.abs(e.clockHours - e.hours) >= 0.05 && (
                      <div className="xs muted">clock says {h(e.clockHours)}</div>
                    )}
                  </td>
                  <td className="xs">
                    <TicketCell entry={e} />
                  </td>
                  <td className="xs">{e.account ?? '—'}</td>
                  <td className="xs">{stampOf(e.created)}</td>
                  <td className="xs">
                    <StatusChip band={lagBand(e.lagDays)}>{lagText(e.lagDays)}</StatusChip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function TicketCell({ entry }: { entry: Entry }) {
  const label = entry.ticketNumber ?? (entry.kind === 'task' ? 'Project task' : 'No ticket');
  return (
    <>
      {entry.link ? (
        <a href={entry.link} target="_blank" rel="noreferrer">
          {label}
        </a>
      ) : (
        <span className="muted">{label}</span>
      )}
      {entry.title && <div className="xs muted tc-title">{entry.title}</div>}
    </>
  );
}

/**
 * One day's entries laid end to end against the clock.
 *
 * Entries are packed into lanes greedily — an entry goes in the first lane
 * whose last bar has already finished — so two bars share a lane only when
 * they cannot overlap. A second lane is therefore the overlap itself, drawn.
 */
function DayTimeline({ day }: { day: DayRow }) {
  const start = Date.parse(day.spanStart);
  const end = Date.parse(day.spanEnd);
  const span = Math.max(1, end - start);
  const lanes: { end: number; bars: (Entry & { left: number; width: number })[] }[] = [];
  for (const e of day.entries) {
    const from = Date.parse(e.start ?? '');
    const to = Date.parse(e.end ?? '');
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
    const bar = { ...e, left: ((from - start) / span) * 100, width: Math.max(0.5, ((to - from) / span) * 100) };
    const lane = lanes.find((l) => l.end <= from);
    if (lane) {
      lane.bars.push(bar);
      lane.end = to;
    } else {
      lanes.push({ end: to, bars: [bar] });
    }
  }

  return (
    <div className="tc-day mt-3">
      <div className="row-between xs muted">
        <span>
          {clockOf(day.spanStart)} – {clockOf(day.spanEnd)}
        </span>
        <span>
          {h(day.hours)} claimed over {h(day.clockHours)} of clock time
        </span>
      </div>
      <div className="tc-lanes mt-1">
        {lanes.map((lane, i) => (
          <div key={i} className="tc-lane">
            {lane.bars.map((b) => (
              <span
                key={b.id}
                className={`tc-bar${b.overlapsWith.length ? ' is-clash' : ''}`}
                style={{ left: `${b.left}%`, width: `${b.width}%` }}
                title={`${clockOf(b.start)} – ${clockOf(b.end)} · ${b.ticketNumber ?? 'no ticket'} · ${b.account ?? 'no account'} · ${h(b.hours)}`}
              >
                <span className="tc-bar-label">{b.ticketNumber ?? b.workType}</span>
              </span>
            ))}
          </div>
        ))}
      </div>
      <p className="xs muted mt-1">
        A second row is an overlap: bars share a row only when their clocks cannot clash. Hover a bar for its ticket and times.
      </p>
    </div>
  );
}
